import React, { useEffect, useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, TrendingDown, Users, MessageSquare, BarChart, Trophy, FileText } from 'lucide-react';

interface AgentMessage {
  id: string;
  agent: string;
  agentInfo: {
    name: string;
    role: string;
    avatar: string;
    color: string;
    icon?: any;
  };
  type: string;
  message: string;
  timestamp: string;
  data?: any;
}

export default function MockAgentConversation() {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const mockMessages: AgentMessage[] = [
    {
      id: '1',
      agent: 'web-analyst',
      agentInfo: {
        name: 'WebAnalyst',
        role: 'Analytics Expert',
        avatar: '/crew-agents/webanalytics.png',
        color: 'bg-blue-500',
        icon: null
      },
      type: 'insight:detected',
      message: 'Detected anomaly: Educational Toys category showing high traffic (2,847 visits/week) but low conversion rate (0.8% vs 3.2% site average)',
      timestamp: new Date().toISOString(),
      data: {
        category: 'Educational Toys',
        traffic: 2847,
        conversionRate: 0.8,
        siteAverage: 3.2
      }
    },
    {
      id: '2',
      agent: 'web-analyst',
      agentInfo: {
        name: 'WebAnalyst',
        role: 'Analytics Expert',
        avatar: '/crew-agents/webanalytics.png',
        color: 'bg-blue-500',
        icon: null
      },
      type: 'question:raised',
      message: 'Why are visitors browsing Educational Toys extensively but not purchasing? Need customer insights to understand barriers.',
      timestamp: new Date(Date.now() + 1000).toISOString()
    },
    {
      id: '3',
      agent: 'know-your-customer',
      agentInfo: {
        name: 'KYC Survey',
        role: 'Customer Research',
        avatar: '/crew-agents/kyc-survey.jpg',
        color: 'bg-purple-500',
        icon: null
      },
      type: 'survey:created',
      message: 'Created targeted survey for Educational Toys visitors with 5 key questions about purchase hesitation',
      timestamp: new Date(Date.now() + 2000).toISOString(),
      data: {
        questions: [
          { id: 1, question: 'What concerns do you have about our educational toys?', type: 'Open-ended' },
          { id: 2, question: 'How do our prices compare to your expectations?', type: 'Scale 1-5' },
          { id: 3, question: 'What information would help you make a purchase decision?', type: 'Multiple choice' },
          { id: 4, question: 'Have you compared our products with competitors?', type: 'Yes/No' },
          { id: 5, question: 'What\'s most important: price, quality, or educational value?', type: 'Ranking' }
        ]
      }
    },
    {
      id: '4',
      agent: 'know-your-customer',
      agentInfo: {
        name: 'KYC Survey',
        role: 'Customer Research',
        avatar: '/crew-agents/kyc-survey.jpg',
        color: 'bg-purple-500',
        icon: null
      },
      type: 'owner:notification',
      message: '@Matt - Survey ready for your review. Would you like to approve these questions for Educational Toys visitors?',
      timestamp: new Date(Date.now() + 3000).toISOString()
    },
    {
      id: '5',
      agent: 'matt',
      agentInfo: {
        name: 'Matt Thompson',
        role: 'ToyWorld Owner',
        avatar: '',
        color: 'bg-green-500',
        icon: null
      },
      type: 'owner:response',
      message: 'Looks great! Please add one more question about warranty concerns. Then go ahead and activate it.',
      timestamp: new Date(Date.now() + 4000).toISOString()
    },
    {
      id: '6',
      agent: 'know-your-customer',
      agentInfo: {
        name: 'KYC Survey',
        role: 'Customer Research',
        avatar: '/crew-agents/kyc-survey.jpg',
        color: 'bg-purple-500',
        icon: null
      },
      type: 'survey:updated',
      message: 'Added warranty question and activated survey. Now collecting responses from Educational Toys visitors.',
      timestamp: new Date(Date.now() + 5000).toISOString()
    },
    {
      id: '7',
      agent: 'system',
      agentInfo: {
        name: 'System',
        role: 'Orchestrator',
        avatar: '',
        color: 'bg-gray-500'
      },
      type: 'time:lapse',
      message: '🕐 3 days later - Survey completed with 127 responses',
      timestamp: new Date(Date.now() + 6000).toISOString()
    },
    {
      id: '8',
      agent: 'know-your-customer',
      agentInfo: {
        name: 'KYC Survey',
        role: 'Customer Research',
        avatar: '/crew-agents/kyc-survey.jpg',
        color: 'bg-purple-500',
        icon: null
      },
      type: 'insights:revealed',
      message: 'Survey results: 73% concerned about product quality vs competitors, 68% unsure about warranty terms, 54% perceive competitors as offering better value',
      timestamp: new Date(Date.now() + 7000).toISOString(),
      data: {
        topConcerns: [
          { issue: 'Quality perception vs competitors', percentage: 73 },
          { issue: 'Warranty uncertainty', percentage: 68 },
          { issue: 'Value perception', percentage: 54 }
        ]
      }
    },
    {
      id: '9',
      agent: 'matt',
      agentInfo: {
        name: 'Matt Thompson',
        role: 'ToyWorld Owner',
        avatar: '',
        color: 'bg-green-500',
        icon: null
      },
      type: 'owner:insight',
      message: 'This is eye-opening! Our educational toys have 5-year warranties and exceed safety standards. We need to communicate this better!',
      timestamp: new Date(Date.now() + 8000).toISOString()
    },
    {
      id: '10',
      agent: 'web-analyst',
      agentInfo: {
        name: 'WebAnalyst',
        role: 'Analytics Expert',
        avatar: '/crew-agents/webanalytics.png',
        color: 'bg-blue-500',
        icon: null
      },
      type: 'analysis:updated',
      message: 'Key insight: Conversion drops 82% when visitors view competitor sites. Main competitor identified: LearnPlayGrow.com',
      timestamp: new Date(Date.now() + 9000).toISOString()
    },
    {
      id: '11',
      agent: 'competitor-analyst',
      agentInfo: {
        name: 'CompetitorAnalyst',
        role: 'Market Intelligence',
        avatar: '/crew-agents/competitors.png',
        color: 'bg-orange-500',
        icon: null
      },
      type: 'analysis:initiated',
      message: 'Analyzing LearnPlayGrow.com educational toys section for comparison...',
      timestamp: new Date(Date.now() + 10000).toISOString()
    },
    {
      id: '12',
      agent: 'competitor-analyst',
      agentInfo: {
        name: 'CompetitorAnalyst',
        role: 'Market Intelligence',
        avatar: '/crew-agents/competitors.png',
        color: 'bg-orange-500',
        icon: null
      },
      type: 'comparison:complete',
      message: 'Analysis complete: ToyWorld offers 5-year warranty vs competitor\'s 1-year. ToyWorld toys exceed ASTM F963 standards. Competitor prices 15% lower but uses cheaper materials.',
      timestamp: new Date(Date.now() + 11000).toISOString(),
      data: {
        comparison: {
          warranty: { toyworld: '5 years', competitor: '1 year' },
          safety: { toyworld: 'Exceeds ASTM F963', competitor: 'Meets minimum' },
          price: { toyworld: 'Premium', competitor: '15% lower' },
          materials: { toyworld: 'Non-toxic, sustainable', competitor: 'Standard plastics' }
        }
      }
    },
    {
      id: '13',
      agent: 'counter-narrative',
      agentInfo: {
        name: 'CounterNarrative',
        role: 'Content Strategist',
        avatar: '/crew-agents/counter-narrative.jpg',
        color: 'bg-indigo-500',
        icon: null
      },
      type: 'strategy:proposed',
      message: 'Identified key differentiation opportunities. Proposing content strategy to highlight ToyWorld\'s superior value proposition.',
      timestamp: new Date(Date.now() + 12000).toISOString()
    },
    {
      id: '14',
      agent: 'counter-narrative',
      agentInfo: {
        name: 'CounterNarrative',
        role: 'Content Strategist',
        avatar: '/crew-agents/counter-narrative.jpg',
        color: 'bg-indigo-500',
        icon: null
      },
      type: 'content:suggested',
      message: 'Created 3 content pieces: "Why Our 5-Year Warranty Matters", "Safety Beyond Standards", and "True Value in Educational Toys". Ready for Educational Toys category pages.',
      timestamp: new Date(Date.now() + 13000).toISOString(),
      data: {
        contentPieces: [
          {
            title: 'Why Our 5-Year Warranty Matters',
            focus: 'Long-term value and confidence in quality'
          },
          {
            title: 'Safety Beyond Standards',
            focus: 'Exceeding ASTM F963 requirements'
          },
          {
            title: 'True Value in Educational Toys',
            focus: 'Cost per year of safe, durable play'
          }
        ]
      }
    },
    {
      id: '15',
      agent: 'matt',
      agentInfo: {
        name: 'Matt Thompson',
        role: 'ToyWorld Owner',
        avatar: '',
        color: 'bg-green-500',
        icon: null
      },
      type: 'owner:approval',
      message: 'Excellent analysis! Let\'s implement this content strategy immediately. Also, add warranty badges to all educational toy listings.',
      timestamp: new Date(Date.now() + 14000).toISOString()
    },
    {
      id: '16',
      agent: 'system',
      agentInfo: {
        name: 'System',
        role: 'Orchestrator',
        avatar: '',
        color: 'bg-gray-500'
      },
      type: 'implementation:complete',
      message: '✅ Content published, warranty badges added. Monitoring conversion impact...',
      timestamp: new Date(Date.now() + 15000).toISOString()
    }
  ];

  useEffect(() => {
    // Auto-play the conversation
    if (currentIndex < mockMessages.length) {
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, mockMessages[currentIndex]]);
        setCurrentIndex(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        // Smooth scroll to bottom
        scrollElement.scrollTo({
          top: scrollElement.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  }, [messages]);

  const getMessageStyle = (agent: string) => {
    const styles: Record<string, string> = {
      'matt': 'bg-green-100',
      'web-analyst': 'bg-blue-100',
      'know-your-customer': 'bg-purple-100',
      'competitor-analyst': 'bg-orange-100',
      'counter-narrative': 'bg-indigo-100',
      'system': 'bg-gray-100'
    };
    return styles[agent] || 'bg-gray-100';
  };

  return (
    <div className="container mx-auto p-6">
      <Card className="bg-white border border-gray-200 rounded-lg shadow-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">AI</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Agent Insights Hub</h3>
              <p className="text-sm text-gray-500">Multi-Agent Collaboration in Action</p>
            </div>
            <Badge variant="default" className="bg-green-500">
              <CheckCircle className="w-3 h-3 mr-1" />Live
            </Badge>
          </div>
          <Badge variant="outline" className="text-gray-600">
            ToyWorld.com - Educational Toys Optimization
          </Badge>
        </div>

        <ScrollArea className="h-[600px]" ref={scrollAreaRef}>
          <div className="p-4 space-y-4">
            {messages.map((message, index) => {
              const isOwner = message.agent === 'matt';
              const isSystem = message.agent === 'system';
              const Icon = message.agentInfo.icon;
              
              return (
                <div key={message.id} className="animate-fade-in">
                  {isSystem ? (
                    <div className="text-center my-4">
                      <Badge variant="outline" className="bg-gray-50">
                        {message.message}
                      </Badge>
                    </div>
                  ) : (
                    <div className={`flex items-start space-x-3 ${isOwner ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      <Avatar className="w-8 h-8">
                        {message.agentInfo.avatar ? (
                          <AvatarImage src={message.agentInfo.avatar} alt={message.agentInfo.name} />
                        ) : null}
                        <AvatarFallback className={`${message.agentInfo.color} text-white`}>
                          {message.agent === 'matt' ? 'MT' : message.agentInfo.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className={`flex-1 ${isOwner ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center gap-2 mb-1 ${isOwner ? 'justify-end' : 'justify-start'}`}>
                          <span className="font-semibold text-gray-900">{message.agentInfo.name}</span>
                          <span className="text-xs text-gray-500">{message.agentInfo.role}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        
                        <div className={`inline-block ${getMessageStyle(message.agent)} rounded-lg px-3 py-2 max-w-md`}>
                          <p className="text-sm">{message.message}</p>
                          
                          {message.data && (
                            <div className="mt-2 space-y-2">
                              {message.data.questions && (
                                <div className="bg-white bg-opacity-70 rounded-lg p-3 mt-2">
                                  <p className="text-xs font-semibold mb-2 text-gray-700">Proposed Survey Questions:</p>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-gray-300">
                                        <th className="text-left pb-2 pr-2 font-medium text-gray-700">#</th>
                                        <th className="text-left pb-2 pr-2 font-medium text-gray-700">Question</th>
                                        <th className="text-left pb-2 font-medium text-gray-700">Type</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {message.data.questions.map((q: any) => (
                                        <tr key={q.id} className="border-b border-gray-200 last:border-b-0">
                                          <td className="py-2 pr-2 font-medium">{q.id}</td>
                                          <td className="py-2 pr-2">{q.question}</td>
                                          <td className="py-2 text-gray-600">
                                            <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs">
                                              {q.type}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              
                              {message.data.topConcerns && (
                                <div className="text-xs space-y-1">
                                  {message.data.topConcerns.map((concern: any, i: number) => (
                                    <div key={i} className="flex justify-between">
                                      <span>{concern.issue}</span>
                                      <span className="font-medium">{concern.percentage}%</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {message.data.comparison && (
                                <div className="text-xs space-y-1">
                                  <div className="grid grid-cols-3 gap-2 font-medium">
                                    <span></span>
                                    <span>ToyWorld</span>
                                    <span>Competitor</span>
                                  </div>
                                  {Object.entries(message.data.comparison).map(([key, value]: [string, any]) => (
                                    <div key={key} className="grid grid-cols-3 gap-2">
                                      <span className="capitalize">{key}:</span>
                                      <span className="text-green-700">{value.toyworld}</span>
                                      <span className="text-red-700">{value.competitor}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {message.data.contentPieces && (
                                <div className="text-xs space-y-1">
                                  {message.data.contentPieces.map((piece: any, i: number) => (
                                    <div key={i} className="p-1 bg-white bg-opacity-50 rounded">
                                      <div className="font-medium">{piece.title}</div>
                                      <div className="text-gray-600">{piece.focus}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {currentIndex >= mockMessages.length && (
              <div className="text-center mt-6 animate-fade-in">
                <Badge variant="default" className="bg-gradient-to-r from-green-500 to-blue-500">
                  <Trophy className="w-4 h-4 mr-2" />
                  Collaborative Intelligence at Work
                </Badge>
              </div>
            )}
          </div>
        </ScrollArea>
      </Card>
      
      <div className="mt-4 text-center">
        <Button 
          onClick={() => {
            setMessages([]);
            setCurrentIndex(0);
          }}
          variant="outline"
        >
          Replay Conversation
        </Button>
      </div>
    </div>
  );
}